import { Box, MenuItem, Stack, TextField, useTheme, useMediaQuery } from "@mui/material";
import { uniqBy } from "../../../utils/lodash-replacements";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { VipTagSelect } from "../../../components/VipTagSelect";
import { useActiveFBOs } from "../../../containers/ActiveFBOContainer";
import { useApi } from "../../../containers/ApiContainer";
import { useLocationsState } from "../../../containers/LocationsStateContainer";
import { fetchAircraft } from "../../../hooks/api";
import { useTenants } from "../../../hooks/useTenants";
import { useVipTags } from "../../../hooks/useVipTags";
import { Hangar, Location, Ramp, Tenant } from "../../../types";
import { addTenantConformationText } from "../../../utils/phrasing";
import { LocationTextField } from "../../../widgets/LocationTextField";
import { SelectTailNumber } from "../../../widgets/SelectTailNumber";
import { Aircraft } from "../../../types";
import { AutocompleteAircraft } from "./AutocompleteAircraft";
import { tenantTypeDropdownColors } from "../../../utils/tripTypeHelpers";

type Props = {
  disableAutocomplete: boolean;
  tailNumber: string;
  setTailNumber: (tailNumber: string) => void;
  owner: string;
  setOwner: (owner: string) => void;
  location: Location<Hangar | Ramp>;
  setShowConfirmation: (message: string) => void;
  type: string;
  setType: (type: string) => void;
  isReference: boolean;
  note: string;
  setNote: (note: string) => void;
  newAircraft: Aircraft;
  setNewAircraft: (aircraft: Aircraft) => void;
  currentLocation: Location<Ramp | Hangar>;
  setCurrentLocation: (l: Location<Ramp | Hangar>) => void;
  assignedLocation: Location<Ramp | Hangar>;
  setAssignedLocation: (l: Location<Ramp | Hangar>) => void;
  vipTagIds: string[];
  setVipTagIds: (ids: string[]) => void;
  autoFocus?: boolean;
};

export const NewAircraftForm: React.FC<Props> = ({
  disableAutocomplete = false,
  tailNumber,
  setTailNumber,
  owner,
  setOwner,
  type,
  setType,
  location,
  setShowConfirmation,
  isReference,
  newAircraft,
  setNewAircraft,
  note,
  setNote,
  assignedLocation,
  setAssignedLocation,
  currentLocation,
  setCurrentLocation,
  vipTagIds,
  setVipTagIds,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const { postgrest } = useApi();
  const { tenants } = useTenants();
  const { locations } = useLocationsState();
  const { activeFBO } = useActiveFBOs();
  const { vipTags } = useVipTags(activeFBO?.id);
  const [shouldFlashAircraft, setShouldFlashAircraft] =
    React.useState<boolean>(false);
  const flashTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
      }
    };
  }, []);

  // for the time being tail numbers aren't unique. we're going to cheat a bit
  // and dedup on tail number here
  const tenantsWithTailNumber = React.useMemo(
    () =>
      uniqBy(
        tenants.filter((t) => t.tail_number),
        "tail_number"
      ),
    [tenants]
  );

  return (
    <Stack direction="column" spacing={isTablet ? 0.75 : 1}>
      <Stack direction="row" spacing={2}>
        <SelectTailNumber
          disableAutocomplete={disableAutocomplete}
          defaultValue={tailNumber}
          tenants={tenantsWithTailNumber}
          autoFocus={autoFocus}
          onInputChange={(tailNumber: string) => {
            setTailNumber(tailNumber);
          }}
          onChange={(tenant: Tenant) => {
            // Set form fields from selected tenant
            setNewAircraft(tenant?.aircraft);
            setType(tenant?.type ?? "");
            setTailNumber(tenant?.tail_number ?? "");
            setOwner(tenant?.owner_manager ?? "");
            setNote(tenant?.note ?? "");
            setVipTagIds(tenant?.vip_tag_ids ?? []);

            // Trigger flash animation when aircraft is available
            if (tenant?.aircraft) {
              if (flashTimeoutRef.current) {
                clearTimeout(flashTimeoutRef.current);
              }
              setShouldFlashAircraft(true);
              flashTimeoutRef.current = setTimeout(() => {
                setShouldFlashAircraft(false);
                flashTimeoutRef.current = null;
              }, 1500);
            }
          }}
        />

        <TextField
          inputProps={{ testid: "owner_manager" }}
          fullWidth
          size="small"
          label={t("settings.hangarSettings.newAircraftForm.labels.ownerManager")}
          onChange={(evt) => setOwner(evt.target.value)}
          value={owner}
        />
        <TextField
          select
          size="small"
          fullWidth
          label={t("settings.hangarSettings.newAircraftForm.labels.type")}
          onChange={(evt) => {
            // Base tenants require confirmation
            if (evt.target.value === "base") {
              const confirmationText = addTenantConformationText(
                !location || location?.type === "hangar" ? "hangar" : "ramp"
              );
              setShowConfirmation(confirmationText);
              return;
            }
            setType(evt.target.value);
          }}
          value={type}
          SelectProps={{
            renderValue: (value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: tenantTypeDropdownColors[value as string] || '#999',
                  flexShrink: 0,
                }} />
                {t(`settings.hangarSettings.newAircraftForm.types.${value}`)}
              </Box>
            ),
          }}
        >
          {(!location || !isReference) && (
            <MenuItem value="transient">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tenantTypeDropdownColors.transient }} />
                {t("settings.hangarSettings.newAircraftForm.types.transient")}
              </Box>
            </MenuItem>
          )}
          {(!location || isReference) && (
            <MenuItem value="base">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tenantTypeDropdownColors.base }} />
                {t("settings.hangarSettings.newAircraftForm.types.base")}
              </Box>
            </MenuItem>
          )}
        </TextField>
        <VipTagSelect
          value={vipTagIds}
          availableTags={vipTags}
          onChange={setVipTagIds}
        />
      </Stack>
      <AutocompleteAircraft
        label={t("settings.hangarSettings.bulkAdd.labels.searchByMakeModel")}
        onSelect={async (aircraft) => {
          // AutocompleteAircraft only fetches partial data (id, make, model, geom, linked_to)
          // We need to fetch the full aircraft record for rendering (wingspan, length, scaled_geom_full, etc.)
          if (aircraft?.id) {
            const fullAircraft = await fetchAircraft(postgrest, aircraft.id, activeFBO);
            setNewAircraft(fullAircraft || aircraft);
          } else {
            setNewAircraft(aircraft);
          }
        }}
        defaultSelected={newAircraft}
        shouldFlash={shouldFlashAircraft}
      />
      <TextField
        margin="dense"
        label={t("settings.hangarSettings.newAircraftForm.labels.notes")}
        type="text"
        fullWidth
        placeholder={t("settings.hangarSettings.newAircraftForm.placeholders.departureTime")}
        multiline
        minRows={isTablet ? 3 : 10}
        onChange={(evt) => setNote(evt.target.value)}
        value={note ?? ""}
      />
      {!location && type === "base" && (
        <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
          <LocationTextField
            isReference={true}
            unassignedText={type === "base" ? t("settings.hangarSettings.newAircraftForm.labels.unassigned") : "N/A"}
            selectedLocationId={assignedLocation?.location_id}
            onChange={(newLocationId) => {
              const newLocation = locations.find(
                (l) => l.location_id === newLocationId
              );
              setAssignedLocation(newLocation);
            }}
          />
          <LocationTextField
            isReference={false}
            selectedLocationId={currentLocation?.location_id}
            unassignedText={t("settings.hangarSettings.newAircraftForm.labels.notHangared")}
            onChange={(newLocationId) => {
              const newLocation = locations.find(
                (l) => l.location_id === newLocationId
              );
              setCurrentLocation(newLocation);
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
